import type { CompanyOptionRepository } from "../../repositories/company/company-option-repository";
import type {
  CreateOptionRequest,
  OptionDetailResponse,
  CreateCombinationRequest,
  CreatePriceTierRequest,
} from "../../types/option";
import { AppError } from "../../../shared/utils/error-handler";

export class CompanyOptionUsecase {
  constructor(private companyOptionRepository: CompanyOptionRepository) {}

  async getOptions(companyId: number): Promise<OptionDetailResponse[]> {
    try {
      return await this.companyOptionRepository.getOptions(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 불러오지 못했습니다.",
        "OPTION_GET_FAILED",
        error,
      );
    }
  }

  async getOptionDetail(companyId: number, optionId: number): Promise<OptionDetailResponse> {
    try {
      return await this.companyOptionRepository.getOptionDetail(companyId, optionId);
    } catch (error) {
      throw new AppError(
        String(error) || "옵션 상세를 불러오지 못했습니다.",
        "OPTION_DETAIL_GET_FAILED",
        error,
      );
    }
  }

  async createOption(
    companyId: number,
    request: CreateOptionRequest,
  ): Promise<OptionDetailResponse> {
    try {
      return await this.companyOptionRepository.createOption(companyId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 생성하지 못했습니다.",
        "OPTION_CREATE_FAILED",
        error,
      );
    }
  }

  async deleteOption(companyId: number, optionId: number): Promise<void> {
    try {
      await this.companyOptionRepository.deleteOption(companyId, optionId);
    } catch (error) {
      throw new AppError(
        String(error) || "옵션을 삭제하지 못했습니다.",
        "OPTION_DELETE_FAILED",
        error,
      );
    }
  }

  async createCombination(
    companyId: number,
    optionId: number,
    request: CreateCombinationRequest,
  ): Promise<OptionDetailResponse> {
    try {
      return await this.companyOptionRepository.createCombination(
        companyId,
        optionId,
        request,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "조합을 생성하지 못했습니다.",
        "COMBINATION_CREATE_FAILED",
        error,
      );
    }
  }

  async deleteCombination(
    companyId: number,
    optionId: number,
    combinationId: number,
  ): Promise<OptionDetailResponse> {
    try {
      return await this.companyOptionRepository.deleteCombination(
        companyId,
        optionId,
        combinationId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "조합을 삭제하지 못했습니다.",
        "COMBINATION_DELETE_FAILED",
        error,
      );
    }
  }

  async bulkCreateCombinationKeys(
    companyId: number,
    optionId: number,
    combinationId: number,
    combinations: { attributeId: number; valueId: number }[][],
  ): Promise<OptionDetailResponse> {
    try {
      return await this.companyOptionRepository.bulkCreateCombinationKeys(
        companyId,
        optionId,
        combinationId,
        combinations,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "조합키를 생성하지 못했습니다.",
        "COMBINATION_KEY_CREATE_FAILED",
        error,
      );
    }
  }

  async bulkCreatePriceTiers(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    priceTiers: CreatePriceTierRequest[],
  ): Promise<OptionDetailResponse> {
    try {
      return await this.companyOptionRepository.bulkCreatePriceTiers(
        companyId,
        optionId,
        combinationId,
        keyId,
        priceTiers,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "가격 구간을 생성하지 못했습니다.",
        "PRICE_TIER_CREATE_FAILED",
        error,
      );
    }
  }
}
